import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBack from '../../components/TopBack/TopBack.jsx';
import { SubscriptionDetailsContext } from '../../services/context/SubscriptionDetailsContext.js';
import ROUTES from '../../services/Constants/GlobalRoutes.jsx';
import { UpdateSnackBarContext } from '../../services/context/SnackBarContext.js';
import FailIcon from '../../assets/FailIcon.jsx';
import './SubsPlanPage.css';
import { defaultAnalyticsVariables, events, pagePrefix } from '../../services/Constants/Analytics.js';
import useNormalizedSubscriptionStatus from '../../services/utils/subscriptionUtils.js';
import { getLabelFromFrequency } from '../../services/Constants/config.js';
import Tick from '../../assets/Tick.jsx';

/**
 * @returns {JSX.Element} SubsPlanPage component
 */
export default function SubsPlanPage() {
  const subscriptionDetails = useContext(SubscriptionDetailsContext);
  const setSnackBar = useContext(UpdateSnackBarContext);
  const navigateTo = useNavigate();
  const subscriptionStatus = useNormalizedSubscriptionStatus();

  // Extract plans from context (for new users)
  const plans = subscriptionDetails?.plans || [];

  const [selectedPlan, setSelectedPlan] = useState(null);

  // Restore selected plan from sessionStorage or default to Monthly
  useEffect(() => {
    if (plans.length > 0) {
      const savedPlan = sessionStorage.getItem('selectedPlan');
      if (savedPlan) {
        const parsedPlan = JSON.parse(savedPlan);
        // Ensure the saved plan still exists in current plans list
        const matchedPlan = plans.find(p => p.packageId === parsedPlan.packageId);
        if (matchedPlan) {
          setSelectedPlan(matchedPlan);
        } else {
          const defaultPlan = plans.find(p => p.frequency === 'M') || plans[0];
          setSelectedPlan(defaultPlan);
        }
      } else {
        const defaultPlan = plans.find(p => p.frequency === 'M') || plans[0];
        setSelectedPlan(defaultPlan);
      }
    }

    window.utag?.view({
      ...defaultAnalyticsVariables,
      page_name: `${pagePrefix}: join vodapay club: choose plan`,
      event_name: [events.pageView],
      visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
    });
  }, [plans]);

  // Persist selected plan in sessionStorage whenever it changes
  useEffect(() => {
    if (selectedPlan) {
      sessionStorage.setItem('selectedPlan', JSON.stringify(selectedPlan));
    }
  }, [selectedPlan]);

  const handleJoinClick = () => {
    const planLabel = getLabelFromFrequency(selectedPlan?.frequency);
    window.utag?.link({
      ...defaultAnalyticsVariables,
      event_name: [events.interaction],
      page_name: `${pagePrefix}: join vodapay club: choose plan `,
      link_id: `${pagePrefix}: join vodapay club: choose plan: ${planLabel}`,
      visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
    });

    if (selectedPlan) {
      navigateTo(ROUTES.SUB_SUMMARY, { state: { selectedPlan } });
    } else {
      setSnackBar({
        icon: <FailIcon />,
        body: 'Service unavailable now. Please try again.',
      });
      window.utag?.link({
        ...defaultAnalyticsVariables,
        event_name: [events.error],
        page_name: `${pagePrefix}: join vodapay club: choose plan `,
        link_id: `${pagePrefix}: join vodapay club: choose plan: ${planLabel}`,
        event_error_name: 'Service unavailable now. Please try again.',
        event_error_type: 'system error',
        visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
      });
    }
  };

  return (
    <div className='vodapay-wrapper'>
      <TopBack />
      <h2 className='vodapay-title'>Choose a VodaPay Club plan</h2>

      <div className='vodapay-plan-list'>
        {
          plans.map(plan => {
            const label = getLabelFromFrequency(plan.frequency);
            const isSelected = selectedPlan?.packageId === plan?.packageId;

            return (
              <div
                key={ plan.packageId }
                className={
                  `vodapay-plan-card voucher container-v ${
                    isSelected ? 'vodapay-selected' : ''
                  }`
                }
                onClick={ () => setSelectedPlan(plan) }
              >
                <div className='vodapay-plan-info'>
                  <h2 className='vodapay-plan-name'>{label}</h2>
                  <h2 className='vodapay-plan-price'>{plan.displayName}</h2>
                </div>

                <div className='vodapay-right-section'>
                  {/* ✅ Show discount if available */}
                  {
                    plan.discountPercentage > 0 && (
                      <span className='vodapay-discount'>Save {plan.discountPercentage}%</span>
                    )
                  }
                  <span
                    className={
                      `vodapay-check-circle ${
                        isSelected ? 'checked' : ''
                      }`
                    }
                  >
                    {isSelected && <Tick />}
                  </span>
                </div>
              </div>
            );
          })
        }
      </div>

      <p className='vodapay-cancel-text'>You can cancel at any time.</p>

      <button
        className='vodapay-join-btn'
        disabled={ !selectedPlan }
        onClick={ handleJoinClick }
      >
        {
          selectedPlan
            ? `Join now - only R${selectedPlan.price}`
            : 'Select a plan'
        }
      </button>
    </div>
  );
}
